// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The bottom navigation, as a model (M3-37, 08 §3).
//
// Separated from the component that draws it because every rule 08 §3 states about this bar is a
// *decision*, and a decision inside JSX is a decision with no test on it: which tabs a width
// gets, which one is active for a path, and whether a destination exists yet.
//
// **Three cues, not one.** "Active and inactive tabs differ by outline versus fill, colour, and a
// top indicator bar — so the active tab is never colour-dependent alone." That is an
// accessibility requirement rather than a styling preference: roughly one man in twelve cannot
// use a red/green difference, and a nav bar whose only signal is an accent colour has no signal
// at all for them. The colour is the third cue here, not the first.
//
// **Five on wide, three on narrow.** Not because five 44 px targets do not fit — they fit in
// 320 px with room over — but because the centre Play tab is deliberately larger, and what is
// left over for the other four below about 400 px is under 56 px each. A 12 px label does not
// survive that, and an unlabelled icon row is a guessing game.

import { coarsePointer } from './pointer'
import type { MessageKey } from './i18n'
// A value import of a module that imports only types from this one, so there is no cycle at run time.
import { tabFor } from './routes'
import { isUnknownGuide } from './machineGuides'

/** Which glyph a tab draws. Generated in code, like every other asset (content rule). */
export type NavIcon = 'arcade' | 'play' | 'boards' | 'build' | 'you'

export interface NavTab {
  readonly id: NavIcon
  /**
   * The 12 px label under the icon, as a bundle key.
   *
   * `MessageKey` rather than `string`, so a tab added with a label nobody translated does not
   * compile. That is the whole reason the type is narrowed: the bottom bar is on every screen, and
   * an English word there is the most visible thing a translation can miss.
   */
  readonly label: MessageKey
  /** The accessible name, which says where it goes rather than repeating the label. */
  readonly title: MessageKey
  readonly to: string
  /**
   * Kept when the bar narrows to three?
   *
   * Arcade, Play and You survive: browse, play, and yourself. Boards and Build are destinations
   * you go to deliberately, and a deliberate destination is reachable from a page rather than
   * needing a permanent seat on a phone that has no room for one.
   */
  readonly essential: boolean
  /** The centre emphasis tab — the most frequent destination (08 §3). */
  readonly emphasis?: boolean
}

/**
 * Every tab, in **bar order**, which is not the order 08 §3's table lists them in.
 *
 * The table reads Arcade · Play · Boards · Build · You and the row beside Play says "the centre
 * emphasis tab". Those two cannot both be positions, and the one that is load-bearing is the
 * second: an emphasis tab is emphasised by being in the middle, under the thumb, and a bar with
 * a raised plate second from the left is a bar with a mistake in it. So Boards moves left of it.
 *
 * It survives the narrowing, too: with Boards and Build dropped, Play is the middle of three.
 *
 * `Play` is `/` because 08 §2 makes the landing route "Landing + instant NEON NOVA play": the
 * fastest route to a ball in play is the front door, and a separate `/play` that the front door
 * also does would be two names for one thing.
 */
/**
 * The tabs, carrying **keys rather than words** since the client gained French.
 *
 * A bottom bar is the last place an untranslated string can hide: it is five words, they are on
 * every screen, and a table in a `.ts` module does not read as copy to anybody scanning components
 * for sentences. `label` and `title` are looked up by the bar that draws them.
 */
export const NAV_TABS: readonly NavTab[] = [
  { id: 'arcade', label: 'nav.tab.arcade', title: 'nav.tab.arcade-title', to: '/arcade', essential: true },
  {
    id: 'boards',
    label: 'nav.tab.boards',
    title: 'nav.tab.boards-title',
    to: '/leaderboards',
    essential: false,
  },
  { id: 'play', label: 'nav.tab.play', title: 'nav.tab.play-title', to: '/', essential: true, emphasis: true },
  { id: 'build', label: 'nav.tab.build', title: 'nav.tab.build-title', to: '/builder', essential: false },
  { id: 'you', label: 'nav.tab.you', title: 'nav.tab.you-title', to: '/settings', essential: true },
]

/**
 * What the footer links to, in the order a person looks for them (S5-01).
 *
 * **Here rather than in `SiteFooter.tsx`, since S5-06.** "What the shell links to" is one
 * question and it now has two askers: the component that draws these, and `src/seo/chrome.ts`,
 * which writes them into the prerendered document so that a crawler has something to follow before
 * JavaScript runs. A `.tsx` module cannot be imported by the Node-side guard that holds those two
 * together — it pulls `react-router` — and a second hand-maintained copy is what the Search
 * Console read of 2026-09-06 was written about.
 *
 * Play first, then the documents. Every label is a `MessageKey`, so a link added with no
 * translation does not compile — the same rule the tabs above follow.
 */
export const FOOTER_LINKS: readonly { readonly to: string; readonly label: MessageKey }[] = [
  { to: '/arcade', label: 'footer.machines' },
  { to: '/how-to-play', label: 'route.title.how-to-play' },
  { to: '/pricing', label: 'route.title.pricing' },
  { to: '/connect', label: 'route.title.connect' },
  { to: '/headset', label: 'route.title.headset' },
  { to: '/support', label: 'route.title.support' },
  { to: '/privacy', label: 'route.title.privacy' },
  { to: '/accessibility', label: 'route.title.accessibility' },
  { to: '/terms', label: 'route.title.terms' },
  { to: '/developers', label: 'route.title.developers' },
  { to: '/about', label: 'route.title.about' },
]

/**
 * Below this CSS width the bar carries three tabs instead of five.
 *
 * 400 rather than a phone model. See the header: it is about what is left for a 12 px label once
 * the emphasis tab has taken its share, not about whether a finger fits.
 */
export const NARROW_MAX_PX = 400

/**
 * At and above this width the bar is gone and the destinations are a horizontal header.
 *
 * 08 §3: "Desktop promotes the same destinations to a horizontal header and drops the bottom
 * bar." A bottom bar on a desktop is a row of buttons an inch from the taskbar, nowhere near
 * where the pointer is.
 */
export const DESKTOP_MIN_PX = 900

/** Which tabs a viewport of this width gets. */
export function tabsFor(cssWidth: number): readonly NavTab[] {
  if (!(cssWidth > 0)) return NAV_TABS
  return cssWidth <= NARROW_MAX_PX ? NAV_TABS.filter((tab) => tab.essential) : NAV_TABS
}

/**
 * Bottom bar, or header?
 *
 * **A width alone gets tablets wrong, and it gave two iPads in the same orientation different
 * answers** (M3-44). 08 §3's rule is "desktop promotes the destinations to a horizontal header and
 * drops the bottom bar", and the reason it gives is about pointers rather than pixels: *"a bottom
 * bar on a desktop is a row of buttons an inch from the taskbar, nowhere near where the pointer
 * is."*
 *
 * Measured against real devices, the width-only rule said: iPad 10.9 portrait (820 px) keeps the
 * bottom bar, iPad Pro 12.9 portrait (1024 px) gets the header. Same posture, same two hands, same
 * thumbs at the bottom corners — and on the larger one every destination moved to the top of a
 * thirteen-inch slab, which is the furthest possible place from where the hands are.
 *
 * So the question is now asked the way the reason is written: a header is for a device being
 * driven by a **pointer**. A touchscreen keeps the bottom bar however large it is, and a desktop
 * that happens to have a touchscreen — `any-pointer: coarse` but `pointer: fine` — is still being
 * driven by a mouse and still gets the header. `coarsePointer` is the right query here, unlike in
 * Settings, where the question is "can this be touched at all".
 *
 * The width term stays, and stays first: a five-destination header does not fit on a phone even
 * with a mouse plugged into it.
 */
export function navPlacement(
  cssWidth: number,
  pointerIsCoarse = coarsePointer(),
): 'bottom' | 'header' {
  if (cssWidth < DESKTOP_MIN_PX) return 'bottom'
  return pointerIsCoarse ? 'bottom' : 'header'
}

/**
 * Is this tab the one the current path is under?
 *
 * `/` is matched exactly and every other destination is matched as a prefix on a **segment
 * boundary**. Both halves matter: a naive `startsWith` would make the Play tab — which is `/` —
 * active on every route in the product, and a naive prefix without the boundary would light
 * `Build` on `/builders-guide`.
 *
 * **Or the route table says the path is under it** (`tabFor`). A prefix cannot see that
 * `/play/iron-fathom` is somewhere you play or that `/machines/neon-nova` belongs to the arcade,
 * and `RouteSpec.tab` has said both all along. Added to the prefix rather than replacing it, so no
 * tab that lit before stops lighting — `/builder/{id}` has no `tab` and still lights Build.
 */
export function isTabActive(tab: NavTab, pathname: string): boolean {
  const path = normalise(pathname)
  const byPrefix = tab.to === '/' ? path === '/' : path === tab.to || path.startsWith(`${tab.to}/`)
  // A guide address naming no machine matches the guide's pattern and renders `NotFound`; a page
  // that is not there sits under no tab, however its address is spelled.
  return byPrefix || (tabFor(path) === tab.id && !isUnknownGuide(path))
}

function normalise(pathname: string): string {
  if (pathname === '' || pathname === '/') return '/'
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}
